import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Observable, Subject } from 'rxjs';
import { map, skip, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ComparatorService } from 'src/app/dashboard/comparator/comparator.service';
import ApiUrls from '../../configs/api-urls.config';
import { ProjectService } from '../../dashboard/project/project.service';
import { ProfileModel } from '../model/profile.model';
import { ResponseV2Interface } from '../model/response.model';
import { SettingsModel } from '../model/settings.model';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class SettingsService implements OnDestroy {
  settings!: SettingsModel;
  onDestroy$: Subject<void> = new Subject();

  constructor(
    private comparatorService: ComparatorService,
    private http: HttpClient,
    private projectService: ProjectService,
    private translationService: TranslationService,
  ) {}

  changeLanguage(language: string): Observable<SettingsModel> {
    return this.http.post<ResponseV2Interface<SettingsModel>>(ApiUrls.profile, { language }).pipe(
      map((res) => res.data),
      tap((data) => (this.settings = data)),
    );
  }

  setActiveComparator(comparatorId: number): Observable<SettingsModel> {
    return this.http
      .post<ResponseV2Interface<SettingsModel>>(ApiUrls.profileSettingsActiveComparator, {
        active_comparator_id: comparatorId,
      })
      .pipe(
        map((res) => res.data),
        tap((data) => (this.settings = data)),
      );
  }

  setActiveProject(projectId: number): Observable<SettingsModel> {
    return this.http
      .post<ResponseV2Interface<SettingsModel>>(ApiUrls.profileSettingsActiveProject, {
        active_project_id: projectId,
      })
      .pipe(
        map((res) => res.data),
        tap((data) => (this.settings = data)),
      );
  }

  setStarredProjects(starred_project_id: number): Observable<SettingsModel> {
    return this.http
      .post<ResponseV2Interface<SettingsModel>>(ApiUrls.profileSettingsStarredProjects, {
        starred_project_id,
      })
      .pipe(
        map((res) => res.data),
        tap((data) => (this.settings = data)),
      );
  }

  unsetStarredProjects(projectId: number): Observable<SettingsModel> {
    return this.http
      .delete<ResponseV2Interface<SettingsModel>>(ApiUrls.profileSettingsStarredProject.prepareUrl({ project: projectId }))
      .pipe(
        map((res) => res.data),
        tap((data) => (this.settings = data)),
      );
  }

  setHoliday(requestData: { holidayFrom: string; holidayTo: string }) {
    return this.http
      .post<ResponseV2Interface<SettingsModel>>(ApiUrls.profileSettingsHoliday, {
        holiday_from: requestData.holidayFrom,
        holiday_to: requestData.holidayTo,
      })
      .pipe(tap(({ data }) => (this.settings = data)));
  }

  setAccountAndProject(request: { account_id: number; active_project_id: number }) {
    return this.http.post<void>(ApiUrls.profileSetAccountAndProject, request);
  }

  changeSettingsInApp(profile: ProfileModel) {
    this.settings = profile.settings;
    const { activeComparator, activeProject, starredProjects } = profile.settings;
    const language = profile.user.language;

    if (language) {
      this.translationService.setCustomLang(language);
    }

    this.stopListening();
    this.comparatorService.activeComparator$.next(activeComparator || null);
    this.projectService.setActiveProject(activeProject || null);
    this.projectService.starredProjects$.next(starredProjects || []);
    this.startListenToChangesInSettings();
  }

  ngOnDestroy(): void {
    this.stopListening();
  }

  private startListenToChangesInSettings() {
    this.comparatorService.activeComparator$
      .pipe(
        takeUntil(this.onDestroy$),
        skip(1),
        switchMap((comparator) => this.setActiveComparator(comparator!.id)),
      )
      .subscribe();

    this.projectService.activeProject$
      .pipe(
        takeUntil(this.onDestroy$),
        skip(1),
        map((project) => (project ? project.id : null)),
        switchMap((projectId) => this.setActiveProject(projectId!)),
      )
      .subscribe();

    this.projectService.starredProjectsAdded$
      .pipe(
        takeUntil(this.onDestroy$),
        map((project) => project.id),
        switchMap((projectId) => this.setStarredProjects(projectId)),
      )
      .subscribe();

    this.projectService.starredProjectsRemoved$
      .pipe(
        takeUntil(this.onDestroy$),
        map((project) => project.id),
        switchMap((projectId) => this.unsetStarredProjects(projectId)),
      )
      .subscribe();
  }

  setSentryData(res: ProfileModel): void {
    const { user, settings } = res;

    Sentry.setUser({ id: user.id, email: user.email });
    const tags = {
      user_email: user?.email,
      user_name: user?.name,
      project_id: settings?.activeProject?.id,
      project_name: settings?.activeProject?.name,
      acting_as: user.last_account,
    };

    Object.entries(tags).forEach(([key, value]) => {
      if (value && value !== '') {
        Sentry.setTag(key, value);
      }
    });
  }

  private stopListening() {
    this.onDestroy$.next();
  }
}
